import React, { Component, useEffect, useRef, useState } from "react"
import ReactDom from "react-dom"
import { SelectionManager, debounce } from "@albelli/photoselector"
import { Size } from "@albelli/list-view-react"
import Close from "../Graphic/close-white.svg"
import LeftArrow from "../Graphic/left-white-arrow.svg"
import RightArrow from "../Graphic/right-white-arrow.svg"
import { PhotoItem } from "../../item"
import { Swipe } from "./Swipe"

// creates portal with element attached to body. In this way the modal will be on top
export const PreviewPortal: React.FunctionComponent<{ children: React.ReactElement<Component> }> = ({ children }) => {
  // need to use already created element perhaps?
  const element = useRef(document.createElement("div"))
  useEffect(() => {
    const container = element.current
    container.classList.add("preview-modal")
    document.body.appendChild(container)
    return () => {
      container.remove()
    }
  })

  return ReactDom.createPortal(children, element.current)
}

interface ImagePreviewProps {
  store: SelectionManager
  item: PhotoItem
  getItems: () => PhotoItem[]
}
// shows preview icon. also toggles showing preview modal
export const ImagePreview: React.FunctionComponent<ImagePreviewProps> = ({ getItems, store, item }) => {
  const [showModal, setShowModal] = useState<boolean>(false)

  const handleClick = (event: React.MouseEvent | KeyboardEvent) => {
    setShowModal(!showModal)
    event.stopPropagation()
  }

  return (
    <div onClick={handleClick} className="photoselector-image-preview-icon" data-tam="image-preview-icon">
      {showModal && (
        <PreviewPortal>
          <Preview item={item} store={store} getItems={getItems} onClose={handleClick}></Preview>
        </PreviewPortal>
      )}
    </div>
  )
}

interface PreviewProps {
  store: SelectionManager
  item: PhotoItem
  getItems(): PhotoItem[]
  onClose: (event: React.MouseEvent | KeyboardEvent) => void
}

interface PreviewState {
  item: PhotoItem
  index: number
  showLeft: boolean
  showRight: boolean
  isSelected: boolean
  size: Size
}

// Preview modal. Handles carousel of images and selection/deselection
export class Preview extends Component<PreviewProps, PreviewState> {
  private readonly keyDownHandler = (event: KeyboardEvent) => this.handleKeyDown(event)
  private readonly resizeDebounced = debounce(() => this.resize(), 300)
  // FIXME: we should not initating property if we are not going to use it
  private swipe: Swipe = new Swipe(
    (event) => this.next(event),
    (event) => this.previous(event)
  )

  constructor(props: PreviewProps) {
    super(props)
    const { item, store } = props
    const items = this.props.getItems()
    const index = items.indexOf(item)
    const showLeft = index > 0
    const showRight = index < items.length - 1
    const isSelected = store.isSelected(item)
    const size = this.calculateDimensions(item.size.width, item.size.height)
    this.state = { item, index, showLeft, showRight, isSelected, size }
  }

  next(event: KeyboardEvent | React.MouseEvent | TouchEvent) {
    const index = this.state.index + 1
    const items = this.props.getItems()
    const item = items[index]
    const showRight = index < items.length - 1
    const showLeft = true
    const isSelected = this.props.store.isSelected(item)
    const size = this.calculateDimensions(item.size.width, item.size.height)
    this.setState({ item, index, showRight, showLeft, isSelected, size })
    event.stopPropagation()
  }

  previous(event: KeyboardEvent | React.MouseEvent | TouchEvent) {
    const index = this.state.index - 1
    const items = this.props.getItems()
    const item = items[index]
    const showLeft = index > 0
    const showRight = true
    const isSelected = this.props.store.isSelected(item)
    const size = this.calculateDimensions(item.size.width, item.size.height)
    this.setState({ item, index, showLeft, showRight, isSelected, size })
    event.stopPropagation()
  }

  calculateDimensions(width: number, height: number): Size {
    let styleWidth: number, styleHeight: number
    const windowRatio = window.innerWidth / window.innerHeight
    const imageRatio = width / height
    if (imageRatio < windowRatio) {
      styleHeight = window.innerHeight * 0.9
      styleWidth = (width / height) * styleHeight
    } else {
      styleWidth = window.innerWidth * 0.9
      styleHeight = (height / width) * styleWidth
    }
    return { width: styleWidth, height: styleHeight }
  }

  toggleSelection(event: KeyboardEvent | React.MouseEvent) {
    const { store } = this.props
    const { item } = this.state
    let isSelected = this.state.isSelected
    if (store.isSelected(item)) {
      store.deselect(item)
      isSelected = false
    } else {
      store.select(item)
      isSelected = true
    }
    this.setState({ isSelected })
    event.stopPropagation()
  }

  handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case "Escape":
        this.props.onClose(event)
        break
      case "ArrowLeft":
        if (this.state.index > 0) this.previous(event)
        break
      case "ArrowRight":
        if (this.state.index < this.props.getItems().length - 1) this.next(event)
        break
      case "Enter":
        this.toggleSelection(event)
        break
      case " ":
        this.toggleSelection(event) // this one for space key
        break
    }

    event.stopPropagation()
    event.preventDefault()
  }

  resize() {
    const { width, height } = this.state.size
    const size = this.calculateDimensions(width, height)
    this.setState({ size })
  }

  componentDidMount() {
    window.addEventListener("keydown", this.keyDownHandler)
    window.addEventListener("resize", this.resizeDebounced)
    this.swipe.addEventListener()
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.keyDownHandler)
    window.removeEventListener("resize", this.resizeDebounced)
    this.swipe.clear()
  }

  render() {
    const { showLeft, showRight, item, isSelected, size } = this.state
    const imageStyle = { width: size.width, height: size.height }
    return (
      <div id="photoselector-image-overlay" className="photoselector-image-preview">
        <div className="photoselector-close-btn" onClick={this.props.onClose}>
          <img src={Close} id="close" className="icon form-error extra-small" />
        </div>
        {showLeft && <img id="prev" className="photoselector-left-arrow" src={LeftArrow} onClick={(event) => this.previous(event)} />}
        <div className="photoselector-image-container" onClick={(ev) => this.toggleSelection(ev)} style={imageStyle}>
          <div className="photoselector-image-preview-selected-icon">
            {isSelected ? (
              <div className="photoselector-deselect-all photoselector-selection-overlay-big"></div>
            ) : (
              <div className="photoselector-select photoselector-selection-overlay-big"></div>
            )}
          </div>
          <img src={item.urlPreview} />
        </div>
        {showRight && <img id="next" className="photoselector-right-arrow" src={RightArrow} onClick={(event) => this.next(event)} />}
      </div>
    )
  }
}
